import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import { throwApiError } from "../../api/api-error";
import { createIdempotencyKey } from "../../api/idempotency";
import type { paths as ApiPaths } from "../../openapi/schema";

type MedicationStockPath = ApiPaths["/patients/{patientId}/medication-stock"];
type MedicationStockBalance = NonNullable<
  NonNullable<
    MedicationStockPath["get"]["responses"][200]["content"]
  >["application/json"]
>["data"]["items"][number];
type MedicationStockAdjustmentBody = NonNullable<
  NonNullable<
    ApiPaths["/patients/{patientId}/medication-stock/adjustments"]["post"]["requestBody"]
  >["content"]
>["application/json"];
type MedicationStockIntakeBody = NonNullable<
  NonNullable<
    ApiPaths["/patients/{patientId}/medication-stock/lots"]["post"]["requestBody"]
  >["content"]
>["application/json"];
type MedicationStockIntakeResult = NonNullable<
  NonNullable<
    ApiPaths["/patients/{patientId}/medication-stock/lots"]["post"]["responses"][200]["content"]
  >["application/json"]
>["data"];

export type { MedicationStockBalance, MedicationStockAdjustmentBody };
export type MedicationStockIntakePayload = Omit<
  MedicationStockIntakeBody,
  "clientRequestId"
> & { clientRequestId?: string };

export const medicationStockKeys = {
  all: ["medication-stock"] as const,
  patient: (patientId: string | null) =>
    [...medicationStockKeys.all, patientId] as const,
};

export function useMedicationStock(options: { enabled?: boolean } = {}) {
  const { client, patientId } = useApiClient();
  const enabled = options.enabled ?? true;

  return useQuery<MedicationStockBalance[]>({
    queryKey: medicationStockKeys.patient(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/medication-stock",
        {
          params: {
            path: { patientId },
            query: { limit: 100 },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to load medication stock",
          "medication-stock",
          response?.status,
        );
      }
      return data?.data.items ?? [];
    },
    enabled: enabled && !!patientId,
  });
}

export function useCreateMedicationStockLot() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    MedicationStockIntakeResult,
    Error,
    MedicationStockIntakePayload
  >({
    mutationFn: async (payload) => {
      if (!patientId) throw new Error("Missing patient ID");
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/medication-stock/lots",
        {
          // The non-versioned default OpenAPI document does not model an
          // x-api-version parameter; pin the version at runtime through the
          // untyped `headers` option, matching useCycles/usePatient.
          headers: { "x-api-version": "2026-06-17" },
          params: {
            path: { patientId },
          },
          body: {
            ...payload,
            clientRequestId: payload.clientRequestId ?? createIdempotencyKey(),
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to save medication stock",
          "medication-stock",
          response?.status,
        );
      }
      if (!data) throw new Error("Medication stock response was empty");
      return data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: medicationStockKeys.patient(patientId),
      });
    },
  });
}

export function useAdjustMedicationStock() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, MedicationStockAdjustmentBody>({
    mutationFn: async (payload) => {
      if (!patientId) throw new Error("Missing patient ID");
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/medication-stock/adjustments",
        {
          params: { path: { patientId } },
          body: payload,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to update medication stock",
          "medication-stock",
          response?.status,
        );
      }
      return data?.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: medicationStockKeys.patient(patientId),
      });
    },
  });
}
